import { useState, useEffect, useRef, createContext, useContext } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  AreaChart, Area,
} from "recharts";

import { T } from './constants/translations';
import { THEMES } from './constants/themes';
import { MOCK_PLAYER, RECOMMENDED_MAPS, ADMIN_USERS } from './constants/mockData';
import { GlobalStyle } from './styles/StyledComponents';
import { computeSkills, computePPTrend, recommendMaps } from './utils/helpers';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Statistics from './components/Statistics';
import Profile from './components/Profile';
import { MapCatalog } from './components/Pages';
import { DevsPage, SearchPage } from './components/Sidebar';


export const LangCtx  = createContext({ lang: "en", t: T.en, setLang: () => {} });
export const useLang  = () => useContext(LangCtx);

export const ThemeCtx = createContext({ theme: THEMES.midnight, themeKey: "midnight", setTheme: () => {} });
export const useTheme = () => useContext(ThemeCtx);

/* ══════════════════════════════════════════
   QUEUE CONTEXT
══════════════════════════════════════════ */
function loadQueue() {
  try {
    const raw = localStorage.getItem('osu_queue');
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveQueue(q) {
  try { localStorage.setItem('osu_queue', JSON.stringify(q)); } catch {}
}

export const QueueCtx = createContext({
  queue: [],
  addToQueue:      () => {},
  removeFromQueue: () => {},
  toggleDone:      () => {},
  isInQueue:       () => false,
});
export const useQueue = () => useContext(QueueCtx);

export const FavMapCtx = createContext({
  favMap: null,
  setFavMap: () => {}
});
export const useFavMap = () => useContext(FavMapCtx);


/* ══════════════════════════════════════════
   PLAYER SEARCH CONTEXT
══════════════════════════════════════════ */
export const PlayerSearchCtx = createContext({
  searchedUser: null,
  setSearchedUser: () => {},
  isViewingOther: false,
  clearSearch: () => {},
});
export const usePlayerSearch = () => useContext(PlayerSearchCtx);

/* ══════════════════════════════════════════
   HELPERS
══════════════════════════════════════════ */
export const fmt = n => n?.toLocaleString() ?? "—";
export const Stars = ({ v }) => (
  <span className="mono" style={{fontSize:12,color:"#ffb6d0",letterSpacing:1}}>
    ✦ {(+v || 0).toFixed(2)}
  </span>
);
export const Notif = ({ msg, onClose }) => {
  useEffect(()=>{ const t=setTimeout(onClose,3000); return()=>clearTimeout(t); },[]);
  return <div className="notif">🌸 {msg}</div>;
};

/* ══════════════════════════════════════════
   THEME-AWARE PARTICLES
   midnight → sakura petals
   dark     → ash particles
   others   → nothing (for now)
══════════════════════════════════════════ */
const SakuraPetals = () => {
  const petals = Array.from({length:12},(_,i)=>({
    id:i,
    left:`${Math.random()*100}%`,
    delay:`${Math.random()*8}s`,
    duration:`${7+Math.random()*8}s`,
    size:`${12+Math.random()*10}px`,
    emoji: ["🌸","🌺","✿","❀"][Math.floor(Math.random()*4)],
  }));
  return (
    <>
      {petals.map(p=>(
        <div key={p.id} className="sakura-petal" style={{
          left:p.left, top:"-20px",
          animationDelay:p.delay, animationDuration:p.duration,
          fontSize:p.size,
        }}>{p.emoji}</div>
      ))}
    </>
  );
};

const AshParticles = () => {
  const particles = Array.from({length:18},(_,i)=>({
    id:i,
    left:`${Math.random()*100}%`,
    delay:`${Math.random()*12}s`,
    duration:`${10+Math.random()*12}s`,
    size:`${4+Math.random()*6}px`,
    opacity: 0.3 + Math.random()*0.4,
    drift: Math.random() > 0.5 ? 'ash-drift-left' : 'ash-drift-right',
  }));
  return (
    <>
      <style>{`
        @keyframes ash-fall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 0.6; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
        @keyframes ash-drift-left {
          0%, 100% { margin-left: 0; }
          50% { margin-left: -30px; }
        }
        @keyframes ash-drift-right {
          0%, 100% { margin-left: 0; }
          50% { margin-left: 30px; }
        }
        .ash-particle {
          position: fixed;
          top: -10px;
          pointer-events: none;
          z-index: 0;
          border-radius: 50%;
          background: radial-gradient(circle, #888 0%, #444 60%, transparent 100%);
          animation: ash-fall linear infinite;
        }
      `}</style>
      {particles.map(p=>(
        <div key={p.id} className="ash-particle" style={{
          left: p.left,
          width: p.size,
          height: p.size,
          opacity: p.opacity,
          animationDelay: p.delay,
          animationDuration: p.duration,
          animation: `ash-fall ${p.duration} ${p.delay} linear infinite`,
        }}/>
      ))}
    </>
  );
};

const ThemeParticles = ({ themeKey }) => {
  if (themeKey === 'midnight') return <SakuraPetals />;
  if (themeKey === 'dark')     return <AshParticles />;
  return null;
};

/* ══════════════════════════════════════════
   PLAYER SEARCH MODAL
══════════════════════════════════════════ */
export const PlayerSearchModal = ({ onClose, onFound }) => {
  const { lang } = useLang();
  const { theme } = useTheme();
  const [query, setQuery]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/search-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: query.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'User not found');
      onFound(data);
      onClose();
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter') handleSearch(); if (e.key === 'Escape') onClose(); };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:2000,
      display:"flex", alignItems:"center", justifyContent:"center",
      background:"rgba(0,0,0,0.85)", backdropFilter:"blur(8px)",
    }} onClick={onClose}>
      <div style={{
        width:420, padding:32, borderRadius:20,
        background:"var(--card2)", border:"2px solid var(--border)",
        boxShadow:`0 0 60px ${theme.accentDim}`,
        position:"relative", overflow:"hidden",
      }} onClick={e=>e.stopPropagation()}>
        {/* glow */}
        <div style={{position:"absolute",inset:0,background:`radial-gradient(circle at 50% 0%,${theme.accentDim},transparent 60%)`,pointerEvents:"none"}}/>

        <h2 className="syne" style={{fontSize:20,fontWeight:800,marginBottom:6,position:"relative"}}>
          🔍 {lang==='ru'?'Поиск игрока':'Search Player'}
        </h2>
        <p style={{fontSize:13,color:"var(--muted)",marginBottom:20,position:"relative"}}>
          {lang==='ru'?'Введи никнейм osu! игрока':'Enter any osu! player username'}
        </p>

        <div style={{display:"flex",gap:10,position:"relative"}}>
          <input
            ref={inputRef}
            value={query}
            onChange={e=>setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder={lang==='ru'?'Никнейм игрока...':'Player username...'}
            style={{flex:1,padding:"10px 14px",borderRadius:12,fontSize:14}}
          />
          <button className="btn-accent" style={{padding:"10px 20px",fontSize:14,borderRadius:12,whiteSpace:"nowrap"}}
            onClick={handleSearch} disabled={loading}>
            {loading ? '⏳' : (lang==='ru'?'Найти':'Find')}
          </button>
        </div>

        {error && (
          <div style={{marginTop:14,padding:"10px 14px",borderRadius:10,fontSize:13,
            background:"rgba(248,113,113,0.1)",border:"1px solid rgba(248,113,113,0.3)",color:"#f87171",
            position:"relative"}}>
            ❌ {error}
          </div>
        )}

        <button onClick={onClose} style={{
          position:"absolute",top:16,right:16,background:"none",border:"none",
          color:"var(--muted)",cursor:"pointer",fontSize:20,lineHeight:1,padding:"0 4px",
        }}>×</button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   LANDING PAGE
══════════════════════════════════════════ */
const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div style={{display:"flex",alignItems:"center",
      background:"var(--card2)",border:"1px solid var(--border)",borderRadius:20,overflow:"hidden"}}>
      {["en","ru"].map(l=>(
        <button key={l} onClick={()=>setLang(l)} style={{
          padding:"4px 12px",fontSize:12,fontWeight:800,
          background:lang===l?"var(--a)":"transparent",
          color:lang===l?"white":"var(--muted)",
          border:"none",cursor:"pointer",fontFamily:"inherit",transition:"all 0.2s",
        }}>{l.toUpperCase()}</button>
      ))}
    </div>
  );
};

const Landing = ({ onLogin }) => {
  const { t } = useLang();
  const { theme } = useTheme();
  const lt = t.landing;

  return (
    <div style={{minHeight:"100vh", position:"relative", overflow:"hidden"}}>
      <div className="grid-bg"/>
      <SakuraPetals/>
      <div className="orb" style={{width:600,height:600,background:`radial-gradient(circle,${theme.accentDim.replace("0.07","0.15")} 0%,transparent 70%)`,top:-200,right:-100}}/>
      <div className="orb" style={{width:400,height:400,background:`radial-gradient(circle,rgba(255,182,193,0.08) 0%,transparent 70%)`,bottom:100,left:-150}}/>

      <nav style={{
        position:"sticky",top:0,zIndex:100,padding:"0 5%",
        height:64,display:"flex",alignItems:"center",justifyContent:"space-between",
        background:`${theme.bg2}cc`,backdropFilter:"blur(16px)",
        borderBottom:"1px solid var(--border)",
      }}>
        <div className="syne" style={{fontSize:20,fontWeight:800,letterSpacing:-0.5}}>
          <span className="accent-text">osu!</span><span style={{color:"var(--text)"}}>nya</span>
        </div>
        <div style={{display:"flex",gap:12,alignItems:"center"}}>
          <LangToggle/>
          <button className="btn-accent" style={{padding:"8px 20px",fontSize:13,borderRadius:10}}
            onClick={onLogin}>{lt.loginBtn}</button>
        </div>
      </nav>

      <section style={{padding:"100px 5% 80px",textAlign:"center",position:"relative",zIndex:1}}>
        <h1 className="hero-title fu d1" style={{fontSize:"clamp(3rem,7vw,6rem)",color:"var(--text)",marginBottom:12}}>
          {lt.hero1}<br/>
          <span className="accent-text">{lt.hero2}</span>
        </h1>
        <p className="fu d2" style={{
          fontSize:"clamp(1rem,2vw,1.2rem)",color:"var(--muted)",
          maxWidth:560,margin:"0 auto 40px",lineHeight:1.7,
        }}>{lt.heroSub}</p>
        <div className="fu d3" style={{display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap"}}>
          <button className="btn-accent" style={{padding:"14px 32px",fontSize:15,borderRadius:12,fontFamily:"'Syne',sans-serif"}}
            onClick={onLogin}>
            <span style={{display:"flex",alignItems:"center",gap:8}}>
              <svg width="18" height="18" viewBox="0 0 32 32" fill="currentColor"><circle cx="16" cy="16" r="16" opacity="0.3"/><circle cx="16" cy="16" r="7"/></svg>
              {lt.loginBtn}
            </span>
          </button>
        </div>
      </section>

      <section style={{padding:"60px 5%",position:"relative",zIndex:1}}>
        <h2 className="syne fu" style={{textAlign:"center",fontSize:32,fontWeight:800,marginBottom:44}}>
          {lt.featTitle}
        </h2>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:20,maxWidth:1000,margin:"0 auto"}}>
          {[
            {icon:"⊙",t:lt.feat1t,d:lt.feat1d,c:"var(--a)"},
            {icon:"◈",t:lt.feat2t,d:lt.feat2d,c:"var(--amber)"},
            {icon:"◉",t:lt.feat3t,d:lt.feat3d,c:"#a78bfa"},
          ].map((f,i)=>(
            <div key={i} className={`feature-card fu d${i+2}`}>
              <div style={{
                width:48,height:48,borderRadius:14,marginBottom:16,
                background:`${f.c}18`,border:`1px solid ${f.c}30`,
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:22,color:f.c,
              }}>{f.icon}</div>
              <h3 className="syne" style={{fontSize:17,fontWeight:700,marginBottom:8}}>{f.t}</h3>
              <p style={{fontSize:13.5,color:"var(--muted)",lineHeight:1.7}}>{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <footer style={{
        borderTop:"1px solid var(--border)",padding:"24px 5%",
        display:"flex",alignItems:"center",justifyContent:"center",
        zIndex:1,position:"relative",
      }}>
        <p style={{fontSize:12,color:"var(--muted)",textAlign:"center"}}>{lt.footer}</p>
      </footer>
    </div>
  );
};

/* ══════════════════════════════════════════
   LOGIN MODAL
══════════════════════════════════════════ */
const LoginModal = ({ onClose, onSuccess }) => {
  const { t } = useLang();
  const { theme } = useTheme();
  const [step, setStep] = useState(0);

  const handleLogin = () => {
    const clientID= '49185';
    const redirectUri = encodeURIComponent('https://www.osu-nya.com/');
    window.location.href = `https://osu.ppy.sh/oauth/authorize?client_id=${clientID}&response_type=code&redirect_uri=${redirectUri}&scope=identify+public`;
  };

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9999,
      display:"flex", alignItems:"center", justifyContent:"center",
      background:"rgba(0,0,0,0.8)", backdropFilter:"blur(6px)",
    }} onClick={onClose}>
      <div className="card" style={{
        width:360, padding:28, position:"relative", overflow:"hidden",
        background:"var(--card2)", borderWidth:2,
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          position:"absolute", inset:0, opacity:0.1, pointerEvents:"none",
          background:`radial-gradient(circle at 70% 20%, ${theme.accent}, transparent 70%)`,
        }}/>
        <h2 className="syne" style={{fontSize:22, fontWeight:800, marginBottom:20, textAlign:"center"}}>
          {step===2 ? "✓ Success!" : "osu! Login"}
        </h2>
        {step===0 && (
          <>
            <p style={{fontSize:13, color:"var(--muted)", marginBottom:24, textAlign:"center"}}>
              Connect your osu! account to unlock analytics.
            </p>
            <button className="btn-accent" style={{width:"100%", padding:"12px", fontSize:14}} onClick={handleLogin}>
              Authorize with osu!
            </button>
          </>
        )}
        {step===1 && (
          <div style={{textAlign:"center", padding:20}}>
            <div style={{
              width:48,height:48, border:"4px solid var(--border)", borderRadius:"50%",
              borderTopColor:theme.accent, margin:"20px auto", animation:"spin 1s linear infinite",
            }}/>
            <p style={{fontSize:13, color:"var(--muted)"}}>Connecting...</p>
          </div>
        )}
        {step===2 && (
          <div style={{textAlign:"center", padding:20}}>
            <div style={{
              width:48,height:48, borderRadius:"50%", background:"#68d89120",
              color:"#68d891", fontSize:28, margin:"20px auto",
            }}>✓</div>
            <p style={{fontSize:13, color:"var(--muted)"}}>Redirecting...</p>
          </div>
        )}
        <button className="btn-ghost" style={{width:"100%", padding:"8px", marginTop:16}}
          onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   TOPBAR
══════════════════════════════════════════ */
const Topbar = ({ title, onMenu, onLogout, username }) => {
  const { theme } = useTheme();
  const { lang } = useLang();

  return (
    <div style={{
      height:58,borderBottom:"1px solid var(--border)",
      background:`${theme.bg2}e0`,backdropFilter:"blur(12px)",
      display:"flex",alignItems:"center",padding:"0 24px",gap:14,
      position:"sticky",top:0,zIndex:100,
    }}>
      <button onClick={onMenu} className="btn-ghost" style={{
        display:"none",padding:"6px 10px",fontSize:16,borderRadius:8,
      }} id="mobile-menu-btn">☰</button>
      <h1 className="syne" style={{fontSize:16,fontWeight:700}}>{title}</h1>

      <div style={{
            marginLeft:"auto",
            display:"flex", alignItems:"center", gap:8,
            background:"var(--card2)", border:"1px solid var(--border)",
            borderRadius:20, padding:"5px 12px", cursor:"pointer",
          }} onClick={onLogout} title="Click to Logout">
      <div style={{
        width:7,
        height:7,
        borderRadius:"50%",
       background:"#4ade80",
        boxShadow:"0 0 6px rgba(74,222,128,0.6)"
      }}/>
      <span style={{fontSize:12,color:"var(--muted)",fontWeight:500}}>
        {username || "sakura_beats"}
                    </span>
                  <span style={{fontSize:10,opacity:0.5,marginLeft:4}}>
              Log out ↪
             </span>
          </div>
      </div>
  );
};

/* ══════════════════════════════════════════
   AUTH LOADING SCREEN
══════════════════════════════════════════ */
const AuthLoading = ({ theme, error, onBack }) => (
  <div style={{
    minHeight:"100vh", display:"flex", flexDirection:"column",
    alignItems:"center", justifyContent:"center",
    background:"var(--bg)", gap:20,
  }}>
    {error ? (
      <>
        <div style={{fontSize:48}}>❌</div>
        <div style={{fontSize:18, fontWeight:700, color:"#f87171"}}>Auth failed</div>
        <div style={{
          fontSize:13, color:"var(--muted)", maxWidth:400,
          textAlign:"center", padding:"12px 20px",
          background:"rgba(248,113,113,0.08)", borderRadius:12,
          border:"1px solid rgba(248,113,113,0.2)",
        }}>{error}</div>
        <button className="btn-accent" style={{padding:"10px 28px", marginTop:8, fontSize:14}}
          onClick={onBack}>
          ← Back to Home
        </button>
      </>
    ) : (
      <>
        <div style={{
          width:52,height:52, border:`3px solid rgba(255,255,255,0.08)`,
          borderRadius:"50%", borderTopColor: theme?.accent || "#ff6eb4",
          animation:"spin 0.9s linear infinite",
        }}/>
        <div style={{fontSize:14, color:"var(--muted)", fontWeight:500}}>Authorizing with osu!...</div>
        <div style={{fontSize:12, color:"var(--muted)", opacity:0.5}}>This may take a few seconds</div>
      </>
    )}
  </div>
);

/* ══════════════════════════════════════════
   APP PROVIDERS — объявлен ВНЕ App()
   Стабильный тип компонента = нет unmount/remount = нет прыжков
══════════════════════════════════════════ */
const AppProviders = ({ lang, t, setLang, theme, themeKey, setThemeKey, queueValue, favMap, setFavMap, playerSearchValue, children }) => (
  <LangCtx.Provider value={{ lang, t, setLang }}>
    <ThemeCtx.Provider value={{ theme, themeKey, setTheme: k => setThemeKey(k) }}>
      <QueueCtx.Provider value={queueValue}>
        <FavMapCtx.Provider value={{ favMap, setFavMap }}>
          <PlayerSearchCtx.Provider value={playerSearchValue}>
            <GlobalStyle theme={theme} />
            {children}
          </PlayerSearchCtx.Provider>
        </FavMapCtx.Provider>
      </QueueCtx.Provider>
    </ThemeCtx.Provider>
  </LangCtx.Provider>
);

/* ══════════════════════════════════════════
   ROOT APP
══════════════════════════════════════════ */
function loadSavedSession() {
  try {
    const raw = localStorage.getItem('osu_session');
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || !s.id || !s.username) { localStorage.removeItem('osu_session'); return null; }
    return s;
  } catch { localStorage.removeItem('osu_session'); return null; }
}

export default function App() {
  const oauthCode = useRef(new URLSearchParams(window.location.search).get('code'));
  const savedSession = loadSavedSession();

  const [lang, setLangRaw] = useState(() => localStorage.getItem('osu_lang') || 'en');
  const [themeKey, setThemeKeyRaw] = useState(() => localStorage.getItem('osu_theme') || 'midnight');
  
  const [userData, setUserData] = useState(savedSession);
  const [loggedIn, setLoggedIn] = useState(!!savedSession && !oauthCode.current);
  
  const [isAuthLoading, setIsAuthLoading] = useState(!!oauthCode.current);
  const [authError, setAuthError] = useState(null);
  const [page, setPageRaw] = useState(() => {
    const saved = localStorage.getItem('osu_page');
    const valid = ['dashboard','profile','stats','maps','search','devs'];
    return (saved && valid.includes(saved)) ? saved : 'dashboard';
  });
  const setPage = (p) => { setPageRaw(p); localStorage.setItem('osu_page', p); };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchedUser, setSearchedUser] = useState(null);

  const [banData, setBanData] = useState(null);

  // Supabase sync
  useEffect(() => {
    if (!loggedIn || !userData?.id) return;

    const syncUser = async () => {
      // Получаем IP на клиенте (безопасно — только для аналитики)
      let userIp = 'unknown';
      try {
        const ipRes  = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipRes.json();
        userIp = ipData.ip;
      } catch {}

      try {
        const res = await fetch('/api/sync-user', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            osu_id:       userData.id,
            username:     userData.username,
            themeKey,
            ip:           userIp,
            access_token: userData.accessToken || null,
          }),
        });

        if (!res.ok) return;
        const data = await res.json();

        // Бан
        if (data.is_banned) {
          setBanData(data.ban_reason || (lang === 'ru' ? 'Бан' : 'Banned'));
          return;
        }

        // Тема из базы
        if (data.theme) setThemeKeyRaw(data.theme);

        // joined_at — только если изменилось
        if (data) {
          setUserData(prev => {
            if (!prev) return prev;
            const updated = { ...prev };
            if (data.joined_at) updated.trackerJoinedAt = data.joined_at;
            updated.profileStatus  = data.status          || '';
            updated.profileSocials = data.socials         || {};
            updated.profileWidgets = data.profile_widgets || ['topplay', 'stats', 'skills'];
            updated.profileColor   = data.profile_color   || null;
            updated.profileFavMap        = data.fav_map        || null;
            updated.profileCustomBanner = data.custom_banner  || null;
            // Сохраняем только если что-то изменилось
            try { localStorage.setItem('osu_session', JSON.stringify(updated)); } catch {}
            return updated;
          });
        if (data.fav_map !== undefined) {
            setFavMapState(data.fav_map || null);
          }
        }

      } catch (e) {
        console.error('[syncUser]', e.message);
      }
    };

    syncUser();
  }, [loggedIn, userData?.id]);

  const setLang = (l) => { setLangRaw(l); localStorage.setItem('osu_lang', l); };
  
  const setThemeKey = async (k) => {
    setThemeKeyRaw(k);
    localStorage.setItem('osu_theme', k);
    // Сохраняем тему через API route
    if (userData?.id) {
      try {
        await fetch('/api/sync-user', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            osu_id:   userData.id,
            username: userData.username,
            themeKey: k,
          }),
        });
      } catch {}
    }
  };

  const [petals, setPetalsRaw] = useState(() => localStorage.getItem('osu_petals') !== 'false');
  const setPetals = (v) => {
    const val = typeof v === 'function' ? v(petals) : v;
    setPetalsRaw(val);
    localStorage.setItem('osu_petals', val ? 'true' : 'false');
  };

  const [queue, setQueue] = useState(loadQueue);


  const addToQueue = (map) => {
    setQueue(prev => {
      if (prev.find(m => m.id === map.id)) return prev;
      const next = [...prev, { ...map, addedAt: Date.now(), done: false }];
      saveQueue(next);
      return next;
    });
  };
  const removeFromQueue = (id) => {
    setQueue(prev => { const next = prev.filter(m => m.id !== id); saveQueue(next); return next; });
  };
  const toggleDone = (id) => {
    setQueue(prev => {
      const next = prev.map(m => m.id === id ? { ...m, done: !m.done } : m);
      saveQueue(next);
      return next;
    });
  };
  const isInQueue = (id) => queue.some(m => m.id === id);
  

  const [favMap, setFavMapState] = useState(() => {
  try {
    const s = JSON.parse(localStorage.getItem('osu_session') || '{}');
    return s.profileFavMap || null;
  } catch {
    return null;
  }
});

const setFavMap = (map) => {
  setFavMapState(map);

  // Supabase
  if (userData?.id) {
    fetch('/api/sync-user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        osu_id: userData.id,
        username: userData.username,
        profileUpdate: { fav_map: map }
      }),
    }).catch(() => {});
  }

  // localStorage
  try {
    const s = JSON.parse(localStorage.getItem('osu_session') || '{}');
    localStorage.setItem('osu_session', JSON.stringify({
      ...s,
      profileFavMap: map
    }));
  } catch {}
};

  // Player search
  const isViewingOther = !!searchedUser;
  const clearSearch = () => setSearchedUser(null);
  const activeUser = searchedUser || userData;

  // OAuth flow
  useEffect(() => {
    const code = oauthCode.current;
    if (!code) return;
    window.history.replaceState({}, document.title, window.location.pathname);
    fetch('/api/get-user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.message || data.detail || 'Server error');
        if (!data.id || !data.username) throw new Error('Invalid response from server');
        localStorage.setItem('osu_session', JSON.stringify(data));
        setUserData(data);
        setLoggedIn(true);
        setIsAuthLoading(false);
        setPage("dashboard");
        oauthCode.current = null;
      })
      .catch(err => {
        console.error("Auth Error:", err.message);
        setAuthError(err.message);
        setIsAuthLoading(false);
        setLoggedIn(false);
        oauthCode.current = null;
      });
  }, []);

  const theme = THEMES[themeKey] || THEMES.midnight;
  const t     = { ...T[lang], lang };
  const titles = {
    dashboard: t.nav.dashboard, profile: t.nav.profile,
    stats: t.nav.stats, maps: t.nav.maps,
    search: lang === 'ru' ? '🔍 Поиск игрока' : '🔍 Search Player',
    devs: 'Devs',
  };;

  const handleLogin  = () => { setLoggedIn(true); setPage("dashboard"); };
  const handleLogout = () => {
    localStorage.removeItem('osu_session');
    setUserData(null); setLoggedIn(false); setPage("dashboard"); setAuthError(null);
  };

  const ensureFreshToken = async () => {
    const session = (() => { try { return JSON.parse(localStorage.getItem('osu_session') || '{}'); } catch { return {}; } })();
    const { accessToken, refreshToken, expiresAt } = session;
    if (accessToken && expiresAt && Date.now() < expiresAt) return accessToken;
    if (refreshToken) {
      try {
        const res  = await fetch('/api/refresh-token', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ refreshToken }) });
        if (!res.ok) throw new Error('refresh failed');
        const data = await res.json();
        const updated = { ...session, accessToken: data.accessToken, refreshToken: data.refreshToken || refreshToken, expiresAt: data.expiresAt };
        localStorage.setItem('osu_session', JSON.stringify(updated));
        setUserData(prev => prev ? { ...prev, ...updated } : prev);
        return data.accessToken;
      } catch (e) {
        console.warn('[ensureFreshToken] failed, forcing logout:', e.message);
        handleLogout(); return null;
      }
    }
    return accessToken || null;
  };

  const handleRefresh = async () => {
  if (isRefreshing || !userData?.id) return;
  setIsRefreshing(true);
  try {
    const token = await ensureFreshToken();
    if (!token) { setIsRefreshing(false); return; }

    const res = await fetch('/api/get-user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken: token }),
    });
    if (!res.ok) throw new Error('Refresh failed');
    const fresh = await res.json();

    const merged = {
      ...fresh,
      trackerJoinedAt:  userData.trackerJoinedAt  || fresh.trackerJoinedAt,
      profileStatus:    userData.profileStatus    || '',
      profileSocials:   userData.profileSocials   || {},
      profileWidgets:   userData.profileWidgets   || ['topplay','stats','skills'],
      profileColor:     userData.profileColor     || null,
      profileFavMap:    userData.profileFavMap    || null,
    };
    localStorage.setItem('osu_session', JSON.stringify(merged));
    setUserData(merged);
  } catch (e) {
    console.error('[refresh]', e.message);
  } finally {
    setIsRefreshing(false);
  }
};


  const queueValue        = { queue, addToQueue, removeFromQueue, toggleDone, isInQueue };
  const playerSearchValue = { searchedUser, setSearchedUser, isViewingOther, clearSearch };
  const providersProps    = { lang, t, setLang, theme, themeKey, setThemeKey, queueValue, favMap, setFavMap, playerSearchValue };

  if (isAuthLoading || authError) {
    return (
      <AppProviders {...providersProps}>
        <AuthLoading theme={theme} error={authError}
          onBack={() => { setAuthError(null); setIsAuthLoading(false); }} />
      </AppProviders>
    );
  }

  if (!loggedIn) {
    return (
      <AppProviders {...providersProps}>
        <div className="grid-bg"/>
        <Landing onLogin={()=>setShowLogin(true)}/>
        {showLogin && <LoginModal onClose={()=>setShowLogin(false)} onSuccess={handleLogin}/>}
      </AppProviders>
    );
  }

  // When user clicks "View Profile" in Search — go to profile page with that user's data
  const handleViewProfile = (foundUser) => {
    setSearchedUser(foundUser);
  };

  const pageMap = {
    dashboard: <Dashboard user={userData || MOCK_PLAYER} onRefresh={handleRefresh} isRefreshing={isRefreshing} />,
    profile:   <Profile   user={activeUser || MOCK_PLAYER} />,
    stats:     <Statistics user={activeUser || MOCK_PLAYER} onRefresh={!isViewingOther ? handleRefresh : null} isRefreshing={isRefreshing} />,
    maps:      <MapCatalog user={activeUser || MOCK_PLAYER} />,
    search:    <SearchPage onViewProfile={handleViewProfile} />,
    devs:      <DevsPage />,
  // 'player' — Dashboard другого игрока (найденного через поиск)
  // onRefresh не передаём — чужие данные не рефрешим
  };

  return (
    <AppProviders {...providersProps}>
      {/* Бан */}
      {banData && (
        <div style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(5, 5, 8, 0.95)',
          backdropFilter: 'blur(10px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 99999, padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#15151a',
            border: '2px solid #ff4444',
            borderRadius: '20px',
            padding: '40px',
            maxWidth: '450px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 0 40px rgba(255, 68, 68, 0.15)'
          }}>
            <h1 style={{ color: '#ff4444', fontSize: '1.8rem', marginBottom: '10px', fontWeight: '900' }}>
              {lang === 'ru' ? 'ДОСТУП ОГРАНИЧЕН' : 'ACCESS RESTRICTED'}
            </h1>
            <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #ff4444, transparent)', marginBottom: '25px' }} />
            <p style={{ color: '#ccc', fontSize: '1.1rem', lineHeight: '1.6' }}>{banData}</p>
            <p style={{ marginTop: '30px', color: '#555', fontSize: '0.85rem' }}>
              {lang === 'ru' ? 'Обжалование:' : 'Appeal:'} <span style={{ color: '#888' }}>gashuser@proton.me</span>
            </p>
          </div>
        </div>
      )}

      <div className="grid-bg"/>
      <div className="orb" style={{width:500,height:500,background:`radial-gradient(circle,${theme.accentDim.replace("0.07","0.08")} 0%,transparent 70%)`,top:-100,right:-100}}/>
      <div className="orb" style={{width:300,height:300,background:`radial-gradient(circle,${theme.amberDim} 0%,transparent 70%)`,bottom:50,left:-100}}/>

      {showLogin && <LoginModal onClose={()=>setShowLogin(false)} onSuccess={handleLogin}/>}


      <div style={{display:"flex",minHeight:"100vh",position:"relative",zIndex:1}}>
        {petals && <ThemeParticles themeKey={themeKey} />}
        <Sidebar page={page} setPage={p=>{ setPage(p); setSidebarOpen(false); }} open={sidebarOpen} petals={petals} setPetals={setPetals}/>
        {sidebarOpen && (
          <div onClick={()=>setSidebarOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:150}}/>
        )}
        <main className="mcol" style={{flex:1,display:"flex",flexDirection:"column",minWidth:0}}>
          <Topbar
            title={titles[page]}
            onMenu={()=>setSidebarOpen(!sidebarOpen)}
            onLogout={handleLogout}
            username={userData?.username}
          />
          <div style={{flex:1,padding:"24px 22px",maxWidth:1180,width:"100%",margin:"0 auto"}} key={page}>
            {pageMap[page]}
          </div>
        </main>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .sidebar { position: fixed !important; }
          #mobile-menu-btn { display: flex !important; }
        }
      `}</style>
    </AppProviders>
  );
}